import { useEffect, useRef, type RefObject } from 'react'

/**
 * Fecha ao clicar fora e ao apertar Esc.
 *
 * Existe porque, antes, janela e menu só fechavam pelo botão de fechar — quem
 * abria "Agendar" ou "Respostas rápidas" sem querer tinha de caçar o X. Clicar
 * fora é o gesto que todo mundo tenta primeiro.
 *
 * A checagem é no `mousedown`, e não no `click`, para não fechar quando a
 * pessoa começa a selecionar um texto dentro da janela e solta o botão fora
 * dela. Nesse caso o `click` é disparado no elemento de fora, mas a intenção
 * era selecionar, não sair.
 */
export function useFecharAoClicarFora<T extends HTMLElement>(
  aberto: boolean,
  aoFechar: () => void,
): RefObject<T | null> {
  const alvo = useRef<T | null>(null)

  useEffect(() => {
    if (!aberto) return

    const noMouseDown = (evento: MouseEvent) => {
      const clicado = evento.target
      if (!(clicado instanceof Node)) return
      if (alvo.current?.contains(clicado)) return
      /*
        Lista e menu abertos flutuam no `body`, fora da caixa de quem os abriu
        (ver `Flutuante`). Um clique numa opção não é "clique fora": sem esta
        linha, escolher uma opção na lista fechava a lista antes da escolha,
        e fechava também a janela em que ela estava.
      */
      if (clicado instanceof Element && clicado.closest('[data-flutuante]')) return
      aoFechar()
    }

    const noTeclado = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') aoFechar()
    }

    document.addEventListener('mousedown', noMouseDown)
    document.addEventListener('keydown', noTeclado)
    return () => {
      document.removeEventListener('mousedown', noMouseDown)
      document.removeEventListener('keydown', noTeclado)
    }
  }, [aberto, aoFechar])

  return alvo
}
